<template>
  <q-page
    :class="{
      'page--search': true,
      'page--search--map-only': showOnlyMap,
    }"
    :style-fn="themeStore.pageStyleFn"
  >
    <component
      :is="componentPageSearch"
      :markers="markers"
      :show-only-map="showOnlyMap"
      :subtitle="pageSubtitle"
      :title="pageTitle"
    />
  </q-page>

  <Teleport
    v-if="$q.screen.lt.md"
    defer
    to=".layout-main-footer__body"
  >
    <SPMapToggleWidget class="map-toggle-widget" />
  </Teleport>
</template>

<script setup lang="ts">
import { useTitle } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { useQuasar } from 'quasar'
import { computed, nextTick, shallowRef, watch, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import SPMapToggleWidget from '@/components/SearchPage/Map/SPMapToggleWidget.vue'
import { useApiFilter } from '@/composables/api/filter'
import { useApiSearch } from '@/composables/api/search'
import { useAsyncComponents } from '@/composables/asyncComponents'
import { useBoot } from '@/composables/boot'
import { useFilter } from '@/composables/filter/index'
import { useSearchPage } from '@/composables/searchPage'
import { useAppStore } from '@/store/modules/app'
import { useThemeStore } from '@/store/modules/theme'
import type {
  IndexSearchVariables,
  ShowMapResponseData,
} from '@/types/api/search'
import { type ComponentPageSearch } from '@/types/async'
import type { PageSearchProps } from '@/types/page'

const props = defineProps<PageSearchProps>()

const { bootSearch } = useBoot()

const { screen } = useQuasar()

const route = useRoute()

const { t } = useI18n()

const { indexSearch, showMap, showSearchMeta } = useApiSearch()

const themeStore = useThemeStore()

const { elMapGoogle, isCentered } = useSearchPage()

const { getComponentPage } = useAsyncComponents()

const {
  configTheme,
  loadingListingsMap,
  loadingListingsMeta,
  loadingListingsSearch,
  responseListingsMap,
  responseListingsMeta,
  responseListingsSearch,
  showOnlyMap,
} = storeToRefs(useAppStore())

const {
  filters,
  filtersLoading,
  getMapVariables,
  getSearchVariables,
  stripInvalidUrlQueryParams,
} = useFilter()

const { index: filterIndex } = useApiFilter()

const componentPageSearch = shallowRef<ComponentPageSearch>()

const markers = computed<ShowMapResponseData[]>(
  () => responseListingsMap.value?.data || [],
)

const pageTitle = computed(() => {
  if (!responseListingsMeta.value) {
    return ''
  }

  const { locations, title } = responseListingsMeta.value

  const _pageTitle = [title]

  if (locations.length === 1) {
    _pageTitle.push(locations[0].label_small)
  }

  return _pageTitle.join(': ')
})

const pageSubtitle = computed(() => {
  const meta = responseListingsSearch.value?.meta

  if (!meta?.total) {
    return ''
  }

  let count =
    responseListingsSearch.value?.data.listings.length
    ?? responseListingsSearch.value?.data.exactMatches.length
    ?? -1

  if (meta.current_page > 1) {
    count += meta.per_page * (meta.current_page - 1)
  }

  return t('manyOfManyProperties', {
    count,
    total: meta.total,
  })
})

const configThemePageSearch = computed(() => configTheme.value?.page.search)

bootSearch(props)

useTitle(pageTitle)

watchEffect(() => {
  const { category, commercialization } = props

  const check = `${category}${commercialization}`.replaceAll(' ', '').length > 0

  if (!check) {
    return
  }

  filtersLoading.value = true

  filterIndex({ category, commercialization })
    .then(({ data }) => {
      filters.value = data.data
    })
    .catch((e) => {
      console.error(e)
    })
    .finally(() => {
      filtersLoading.value = false
    })
})

const queryListingsSearch = (variables: IndexSearchVariables) => {
  loadingListingsSearch.value = true

  indexSearch(variables)
    .then(({ data }) => {
      responseListingsSearch.value = data
    })
    .catch((e) => {
      responseListingsSearch.value = undefined

      console.error(e)
    })
    .finally(() => {
      loadingListingsSearch.value = false
    })
}

const search = () => {
  if (showOnlyMap.value) {
    return
  }

  if (!filters.value.length) {
    setTimeout(() => {
      search()
    }, 100)

    return
  }

  const variables = getSearchVariables()

  queryListingsSearch(variables)
}

const meta = () => {
  if (!filters.value.length) {
    setTimeout(() => {
      meta()
    }, 100)

    return
  }

  loadingListingsMeta.value = true

  const variables = getSearchVariables()

  showSearchMeta(variables)
    .then(({ data }) => {
      responseListingsMeta.value = data.data
    })
    .catch((e) => {
      console.error(e)
    })
    .finally(() => {
      loadingListingsMeta.value = false
    })
}

const geoFilters = computed<string[]>(() =>
  Object.keys(route.query).filter((q) =>
    ['coordinates', 'locations'].includes(q),
  ),
)

const focusOnMarkers = () => {
  nextTick(() => {
    if (!elMapGoogle.value || isCentered.value) {
      return
    }

    isCentered.value = true

    elMapGoogle.value.fitBoundsToMarkers()
  })
}

const clusters = () => {
  if (!showOnlyMap.value && screen.lt.md) {
    return
  }

  if (!filters.value.length) {
    setTimeout(() => {
      clusters()
    }, 100)

    return
  }

  const variables = getMapVariables()

  loadingListingsMap.value = true

  showMap(variables)
    .then(({ data }) => {
      responseListingsMap.value = data

      if (geoFilters.value.length === 1) {
        switch (geoFilters.value[0]) {
          case 'coordinates':
            const coordinates = route.query['coordinates']
              ?.toString()
              .split(',')
            // TODO: validate this globally.
            if (coordinates?.length !== 8) {
              return
            }

            focusOnMarkers()
            break

          case 'locations':
            focusOnMarkers()
            break
        }
      } else {
        focusOnMarkers()
      }
    })
    .catch((e) => {
      console.error(e)

      responseListingsMap.value = undefined
    })
    .finally(() => {
      loadingListingsMap.value = false
    })
}

watch(
  filters,
  (v) => {
    if (!v) {
      return
    }

    stripInvalidUrlQueryParams()
  },
  { deep: true },
)

watch(
  [() => route.query, () => route.params],
  (newValue, oldValue) => {
    search()

    clusters()

    const condition =
      !responseListingsMeta.value
      || newValue[0]['locations'] !== oldValue[0]?.['locations']
      || newValue[1].category !== oldValue[1]?.category
      || newValue[1].commercialization !== oldValue[1]?.commercialization

    if (condition) {
      meta()
    }
  },
  { immediate: true },
)

watch(showOnlyMap, () => {
  search()

  clusters()
})

watchEffect(() => {
  if (!configThemePageSearch.value) {
    return
  }

  componentPageSearch.value = getComponentPage(
    configThemePageSearch.value.component,
  ) as ComponentPageSearch
})

const searchListWidth = computed(
  () => configThemePageSearch.value?.component_props.list[screen.name],
)
const searchHeaderWidth = computed(
  () => configThemePageSearch.value?.component_props.header[screen.name],
)
</script>

<style lang="scss">
.page--search {
  position: relative;

  --search-list-width: v-bind('searchListWidth');
  --search-header-width: v-bind('searchHeaderWidth');
}
</style>
